import type { DevicePorts } from "../../../../runtime/ports";
import type { PlayerQueueType } from "../../utils/queue.types";

export const PLAYBACK_RESUME_CHECKPOINT_KEY =
  "lissen-playback-resume-checkpoint-v2";
const LEGACY_PLAYBACK_RESUME_CHECKPOINT_KEY =
  "lissen-playback-resume-checkpoint-v1";

const MIN_RESUME_POSITION_MS = 5_000;
const END_GUARD_MS = 10_000;

export type PlaybackResumeCheckpoint = {
  version: 2;
  queueType: PlayerQueueType;
  songId: string;
  queueItemId: string;
  positionMs: number;
  durationMs: number;
  updatedAtMs: number;
};

type ResumeTarget = {
  songId: string;
  queueItemId: string;
  durationMs: number;
};

const isFiniteNonNegativeNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value) && value >= 0;

const isPlaybackResumeCheckpoint = (
  value: unknown,
): value is PlaybackResumeCheckpoint => {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<PlaybackResumeCheckpoint>;
  return (
    candidate.version === 2 &&
    (candidate.queueType === "playlist" || candidate.queueType === "foryou") &&
    typeof candidate.songId === "string" &&
    candidate.songId.length > 0 &&
    typeof candidate.queueItemId === "string" &&
    candidate.queueItemId.length > 0 &&
    isFiniteNonNegativeNumber(candidate.positionMs) &&
    isFiniteNonNegativeNumber(candidate.durationMs) &&
    isFiniteNonNegativeNumber(candidate.updatedAtMs)
  );
};

type LegacyPlaybackResumeCheckpoint = Omit<
  PlaybackResumeCheckpoint,
  "version" | "queueType"
> & { version: 1 };

const isLegacyPlaybackResumeCheckpoint = (
  value: unknown,
): value is LegacyPlaybackResumeCheckpoint => {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<LegacyPlaybackResumeCheckpoint>;
  return (
    candidate.version === 1 &&
    typeof candidate.songId === "string" &&
    candidate.songId.length > 0 &&
    typeof candidate.queueItemId === "string" &&
    candidate.queueItemId.length > 0 &&
    isFiniteNonNegativeNumber(candidate.positionMs) &&
    isFiniteNonNegativeNumber(candidate.durationMs) &&
    isFiniteNonNegativeNumber(candidate.updatedAtMs)
  );
};

export const getPlaybackResumeCheckpointKey = (queueType: PlayerQueueType) =>
  `${PLAYBACK_RESUME_CHECKPOINT_KEY}:${queueType}`;

export function createResumeCheckpoint(
  resumeStorage: Pick<DevicePorts["kv"], "getString" | "set" | "remove">,
  now: () => number,
) {
  function clearPlaybackResumeCheckpoint(queueType?: PlayerQueueType): void {
    try {
      if (queueType) {
        resumeStorage.remove(getPlaybackResumeCheckpointKey(queueType));
        resumeStorage.remove(LEGACY_PLAYBACK_RESUME_CHECKPOINT_KEY);
        return;
      }

      resumeStorage.remove(getPlaybackResumeCheckpointKey("playlist"));
      resumeStorage.remove(getPlaybackResumeCheckpointKey("foryou"));
      resumeStorage.remove(LEGACY_PLAYBACK_RESUME_CHECKPOINT_KEY);
    } catch {
      // Resume persistence is best-effort and must never break playback.
    }
  }

  function readPlaybackResumeCheckpoint(
    queueType: PlayerQueueType,
  ): PlaybackResumeCheckpoint | null {
    try {
      const key = getPlaybackResumeCheckpointKey(queueType);
      const raw = resumeStorage.getString(key);
      if (raw) {
        const parsed: unknown = JSON.parse(raw);
        if (
          !isPlaybackResumeCheckpoint(parsed) ||
          parsed.queueType !== queueType
        ) {
          resumeStorage.remove(key);
          return null;
        }
        return parsed;
      }

      // Migrate the single-slot checkpoint used by early preview builds. The
      // active queue is the only identity that old format lacked.
      const legacyRaw = resumeStorage.getString(
        LEGACY_PLAYBACK_RESUME_CHECKPOINT_KEY,
      );
      if (!legacyRaw) return null;
      const legacyParsed: unknown = JSON.parse(legacyRaw);
      resumeStorage.remove(LEGACY_PLAYBACK_RESUME_CHECKPOINT_KEY);
      if (!isLegacyPlaybackResumeCheckpoint(legacyParsed)) {
        return null;
      }

      const migrated: PlaybackResumeCheckpoint = {
        ...legacyParsed,
        version: 2,
        queueType,
      };
      resumeStorage.set(key, JSON.stringify(migrated));
      return migrated;
    } catch {
      clearPlaybackResumeCheckpoint(queueType);
      return null;
    }
  }

  function writePlaybackResumeCheckpoint(
    checkpoint: Omit<PlaybackResumeCheckpoint, "version" | "updatedAtMs"> & {
      updatedAtMs?: number;
    },
  ): void {
    const value: PlaybackResumeCheckpoint = {
      ...checkpoint,
      version: 2,
      updatedAtMs: checkpoint.updatedAtMs ?? now(),
    };
    if (!isPlaybackResumeCheckpoint(value)) return;

    try {
      resumeStorage.set(
        getPlaybackResumeCheckpointKey(value.queueType),
        JSON.stringify(value),
      );
      resumeStorage.remove(LEGACY_PLAYBACK_RESUME_CHECKPOINT_KEY);
    } catch {
      // Resume persistence is best-effort and must never break playback.
    }
  }

  return {
    clearPlaybackResumeCheckpoint,
    readPlaybackResumeCheckpoint,
    writePlaybackResumeCheckpoint,
  };
}
export type ResumeCheckpoint = ReturnType<typeof createResumeCheckpoint>;
export function resolvePlaybackResumePositionMs(
  checkpoint: PlaybackResumeCheckpoint,
  target: ResumeTarget,
): number | null {
  if (
    checkpoint.songId !== target.songId ||
    checkpoint.queueItemId !== target.queueItemId
  ) {
    return null;
  }

  const positionMs = checkpoint.positionMs;
  if (positionMs < MIN_RESUME_POSITION_MS) return null;

  const durationMs =
    target.durationMs > 0 ? target.durationMs : checkpoint.durationMs;
  if (durationMs > 0 && durationMs - positionMs <= END_GUARD_MS) return null;

  return positionMs;
}
